import { Card, CardContent, Separator } from '@mlm/ui';
import { PiggyBank } from 'lucide-react';
import type { TotalSaved } from '../../lib/api';
import { fmtCurrency } from '../../lib/format';

/**
 * "Total saved" card — the lifetime sum of every discount the member received
 * (membership/rank/stockist product prices, coupons, manager discounts) across
 * their online-store and cashier purchases. Rendered only when the tenant's
 * `total_saved_counter_enabled` flag is on (parent gates on the tenant config
 * AND the endpoint's own `enabled` echo). Shows ₱0.00 for members with no
 * savings yet — advertising the mechanic is the point of the card.
 */
export function TotalSavedPanel({ totals }: { totals: NonNullable<TotalSaved['totals']> }) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-2">
          <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
            Total saved
          </p>
          <PiggyBank className="h-4 w-4 text-muted-foreground" />
        </div>

        <div className="mt-2 text-4xl font-semibold leading-none tabular-nums">
          {fmtCurrency(totals.total)}
        </div>

        <Separator className="my-4" />

        <dl className="space-y-2">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">Online store</dt>
            <dd className="font-medium tabular-nums">{fmtCurrency(totals.ecom)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">In-store (cashier)</dt>
            <dd className="font-medium tabular-nums">{fmtCurrency(totals.pos)}</dd>
          </div>
        </dl>

        <p className="mt-3 text-caption text-muted-foreground">
          Everything you&apos;ve saved through member pricing, coupons, and discounts across all
          your purchases.
        </p>
      </CardContent>
    </Card>
  );
}
